import React, { useState } from "react";
import { isMobile, isTablet } from "react-device-detect";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";
import { SpaceCategory, getSpaceCategoryLabel } from "@lifeat/shared/data-models/virtual-space/SpaceCategory";
import { AppearanceContextType, useAppearanceContext } from "@contexts/AppearanceContext";
import { useVirtualSpaceContext } from "@contexts/VirtualSpaceContext";
import { SpaceShuffleButtonSize } from "../../../SpaceSelectionConstants";
import { SpaceCategoryIconResolver } from "../../SpaceCategoryIcon/SpaceCategoryIconResolver";
import ChevronLeftIcon from "/resources/icons/ChevronLeftArrow.svg";
import ChevronRightIcon from "/resources/icons/ChevronRightArrow.svg";
import ShuffleIcon from "/resources/icons/ShuffleIcon.svg";

const CategoryShuffleWrapper = styled.div<{ isDisabled: boolean }>`
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    cursor: pointer;

    ${(props) =>
        props.isDisabled
            ? `
          opacity: 0.5;
        cursor: not-allowed;
    `
            : ""}
`;

const CategoryControlsButton = styled.div<{ isActive: boolean; increaseWidth: boolean }>`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;

    position: relative;
    height: ${SpaceShuffleButtonSize}px;
    width: ${(props) => (props.increaseWidth ? `60px` : `${SpaceShuffleButtonSize}px`)};

    border-radius: 10px;
    cursor: inherit;
    border: 1px solid ${(props) => props.theme.borderPrimary};

    &:hover {
        background: ${(props) => props.theme.hoverSecondary};
    }

    background: ${(props) => (props.isActive ? props.theme.backgroundSecondary : props.theme.backgroundPrimary)};

    transition: width 0.2s ease-in-out;
`;

const ShuffleCategoryButton = styled.div<{ isActiveCategory?: boolean }>`
    display: flex;
    justify-content: center;
    align-items: center;

    position: relative;
    height: 100%;
    width: 50%;

    border: none;

    cursor: inherit;

    &:hover {
        background: ${(props) => props.theme.hoverSecondary};
    }
`;

const ShuffleBackwardsButton = styled(ShuffleCategoryButton)`
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
`;
const ShuffleForwardsButton = styled(ShuffleCategoryButton)`
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
`;

const StyledSpaceCategoryIconResolver = styled(SpaceCategoryIconResolver)<{ isActive?: boolean }>`
    height: 20px;
    width: 20px;
    font-size: 20px;
    position: absolute;

    opacity: ${(props) => (props.isActive ? 0.3 : 1)};
`;

const StyledShuffleIcon = styled(ShuffleIcon)`
    fill: ${(props) => props.theme.iconColor};
    scale: 0.7;
`;

const ShuffleArrowsWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    height: 100%;
    width: 100%;
`;

const StyledChevronLeftIcon = styled(ChevronLeftIcon)`
    fill: ${(props) => props.theme.iconColor};
`;

const StyledChevronRightIcon = styled(ChevronRightIcon)`
    fill: ${(props) => props.theme.iconColor};
`;

interface SpaceCategoryShuffleButtonProps {
    readonly spaceCategory: SpaceCategory;
    readonly isActiveSpaceCategory: boolean; // if the current virtual space being displayed is this category
    readonly selectCategory: () => void;
    readonly logSpaceShuffleEvent: () => void;
    readonly disabled?: boolean;
}
export default function SpaceCategoryShuffleButton(props: SpaceCategoryShuffleButtonProps) {
    const { lightMode }: AppearanceContextType = useAppearanceContext();
    const { spaceController } = useVirtualSpaceContext();

    // Used to animate spinning the shuffle icon for 200ms.
    const [spinForward, setSpinForward] = useState(false);
    const [spinRewind, setSpinRewind] = useState(false);

    const handleShuffleNextClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (props.disabled) {
            return;
        }
        props.logSpaceShuffleEvent();
        const isShuffle = props.isActiveSpaceCategory;
        if (!isShuffle) {
            props.selectCategory();
            // Delay setting the spin classname so the icon can render when switching categories.
            setTimeout(() => setSpinForward(true), 50);
            setTimeout(() => setSpinForward(false), 250);
        } else {
            spaceController.playNextSpaceInCategory(props.spaceCategory);
            // No delay needed due to already being rendered on the button when shuffling
            // same category.
            setSpinForward(true);
            setTimeout(() => setSpinForward(false), 200);
        }
    };

    const handleShufflePrevClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (props.disabled) {
            return;
        }
        props.logSpaceShuffleEvent();
        const isShuffle = props.isActiveSpaceCategory;
        if (!isShuffle) {
            spaceController.playPreviousSpaceInCategory(props.spaceCategory); // Delay setting the spin classname so the icon can render when switching categories.
            setTimeout(() => setSpinRewind(true), 50);
            setTimeout(() => setSpinRewind(false), 250);
        } else {
            spaceController.playPreviousSpaceInCategory(props.spaceCategory);
            // No delay needed due to already being rendered on the button when shuffling
            // same category.
            setSpinRewind(true);
            setTimeout(() => setSpinRewind(false), 200);
        }
    };

    return (
        <CategoryShuffleWrapper
            isDisabled={props.disabled}
            data-tip={getSpaceCategoryLabel(props.spaceCategory)}
            data-for={`space-category-button-${props.spaceCategory}`}
        >
            <ReactTooltip id={`space-category-button-${props.spaceCategory}`} padding={"10px"} />
            {props.disabled && <ReactTooltip id={"space-shuffler-disabled-state-tooltip"} type={"dark"} />}
            <CategoryControlsButton
                id={
                    `space-category-shuffle-button-${props.spaceCategory.toLowerCase()}` // required for Frigade tooltip to anchor to this component.
                }
                isActive={props.isActiveSpaceCategory}
                increaseWidth={props.isActiveSpaceCategory && !(isMobile || isTablet)}
                onClick={handleShuffleNextClick}
            >
                <StyledSpaceCategoryIconResolver
                    category={props.spaceCategory}
                    isActive={props.isActiveSpaceCategory}
                />

                {(isMobile || isTablet) && props.isActiveSpaceCategory && (
                    <StyledShuffleIcon
                        onClick={handleShuffleNextClick}
                        className={`shuffle-icon ${spinForward ? "spin-forward" : ""} ${spinRewind ? "spin-rewind" : ""} ${
                            lightMode ? "light" : "dark"
                        }`}
                    />
                )}

                {!isMobile && !isTablet && props.isActiveSpaceCategory && (
                    <ShuffleArrowsWrapper>
                        <ShuffleBackwardsButton
                            isActiveCategory={props.isActiveSpaceCategory}
                            onClick={handleShufflePrevClick}
                        >
                            <StyledChevronLeftIcon />
                        </ShuffleBackwardsButton>

                        <ShuffleForwardsButton
                            isActiveCategory={props.isActiveSpaceCategory}
                            onClick={handleShuffleNextClick}
                        >
                            <StyledChevronRightIcon />
                        </ShuffleForwardsButton>
                    </ShuffleArrowsWrapper>
                )}
            </CategoryControlsButton>
        </CategoryShuffleWrapper>
    );
}
